import ajax from '../../common/services/httpService'
import utilService from '../../common/services/utilService'

/**
 * Fetch closed room complaints (calls history) for the current shift.
 * @param {number|string} roomId
 * @param {number|string} siteId
 * @returns {Promise<Array<{ time: string, count: number, servikId: number, url?: string, backgroundColor?: string, isSuggestion?: boolean, isOffline?: boolean }>>}
 */
async function getClosedRoomComplaints(roomId, siteId) {
  const data = await ajax.get(`room/realtimeRoom/${roomId}`, null, { siteId })
  return Array.isArray(data) ? data : []
}

/**
 * Site day totals (company-local midnight → now): complaints vs call-cleaner SLAs.
 * @param {number|string} siteId
 * @returns {Promise<{ complaints: number, callCleaners: number }>}
 */
async function getRealTimeDayCounts(siteId) {
    const data = await ajax.get('sla/real-time/day-counts', null, { siteId })
    return {
        complaints: Number(data?.complaints || 0),
        callCleaners: Number(data?.callCleaners || 0),
    }
}

export default {
    getRealTimeData,
    getRealTimeRoomScope,
    getRealTimeDayCounts,
    filterFromByShiftStartHour,
    getCurrentShiftWindow,
    getShiftWindowForDate,
    getShiftQueryDateRange,
    formatElapsed,
    getCleandexData,
    getClosedRoomComplaints,
}


/**
 * HH:MM:SS of an elapsed time. Hours keep counting past 24, so a duration never depends on calendar days or timezones.
 * A small negative value (browser clock behind the server) is a call that just opened.
 * @param {number} ms
 */
function formatElapsed(ms) {
    const totalSeconds = Math.floor(Math.max(0, ms) / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const pad = (value) => String(value).padStart(2, '0')
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

/**
 * @param {Number | String} siteId
 * @param {string} timeFrom
 * @param {'clean' | 'maintenance'} type
 * @param {boolean} isSurveyRealTime
 * @param {boolean} includePastVisits
 * @param {number|string|null} [contactId] Optional contact (cleaner) scope for portal real-time filter
 */
async function getRealTimeData(siteId, timeFrom, type = 'clean', isSurveyRealTime = false, includePastVisits = true, contactId = null) {
    const params = { siteId, type, timeFrom, isSurveyRealTime, includePastVisits }
    if (contactId != null && contactId !== '') {
        params.contactId = contactId
    }
    const realTimeData = await ajax.get('sla/real-time', null, params)
    return realTimeData
}

/**
 * Room ids in current shift scope for a contact at the site (for socket payload filtering).
 * @param {number|string} siteId
 * @param {number|string} contactId
 * @returns {Promise<number[]>}
 */
async function getRealTimeRoomScope(siteId, contactId) {
    const data = await ajax.get('sla/real-time/scope', null, { siteId, contactId })
    if (!data || !Array.isArray(data.roomIds)) {
        return []
    }
    return data.roomIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
}

async function getCleandexData(siteId) {
    const cleandexData = await ajax.get(`sla/cleandex/${siteId}`)
    return cleandexData
}

/**
 * @param {Number} shiftStart
 * @returns {Date}
*/
function filterFromByShiftStartHour(shiftStart, gmt) {
    const now = new Date()
    const timeFrom = new Date(now)

    // If gmt is provided, interpret shiftStart as local time in that GMT offset
    // and set the underlying Date using UTC setters to avoid local timezone shifts.
    if (typeof gmt === 'number' && !Number.isNaN(gmt)) {
        const wholeHours = Math.floor(shiftStart)
        const minutes = Math.round((shiftStart - wholeHours) * 60)

        const utcHour = wholeHours - gmt
        timeFrom.setUTCSeconds(0, 0)
        timeFrom.setUTCMinutes(minutes)
        timeFrom.setUTCHours(utcHour)

        if (timeFrom.getTime() > now.getTime()) {
            timeFrom.setUTCDate(timeFrom.getUTCDate() - 1)
        }
        return timeFrom
    }

    // Fallback to previous local-time behavior if gmt is not provided
    timeFrom.setHours(shiftStart)
    if (Number.isInteger(shiftStart)) {
        timeFrom.setMinutes(0)
    } else {
        timeFrom.setMinutes(Math.round((shiftStart - Math.floor(shiftStart)) * 60))
    }
    if (timeFrom > now) timeFrom.setDate(now.getDate() - 1)
    return timeFrom
}

/**
 * UTC window [start, end] for the currently active shift (from getCurrentShift).
 * start_hour / end_hour are company-local decimals; gmt is the company offset.
 * Reuses filterFromByShiftStartHour for the start anchor (same as Real Time / Overview).
 * @param {{ start_hour: Number, end_hour: Number, gmt?: Number }} shift
 * @returns {{ start: Date, end: Date } | null}
 */
function getCurrentShiftWindow(shift) {
    if (!shift) return null
    const startHour = Number(shift.start_hour)
    const endHour = Number(shift.end_hour)
    const gmt = shift.gmt
    if (!Number.isFinite(startHour) || !Number.isFinite(endHour)) return null

    const start = filterFromByShiftStartHour(startHour, gmt)
    const end = new Date(start)

    if (typeof gmt === 'number' && !Number.isNaN(gmt)) {
        const wholeHours = Math.floor(endHour)
        const minutes = Math.round((endHour - wholeHours) * 60)
        const utcHour = wholeHours - gmt
        end.setUTCSeconds(0, 0)
        end.setUTCMinutes(minutes)
        end.setUTCHours(utcHour)
        // Advance end until it is past start. Do not rely on local start/end hour
        // ordering alone — UTC→company conversion can make an overnight DB shift look
        // same-day locally (e.g. 21→20.75 UTC at GMT+3 becomes 0→23.75).
        while (end.getTime() <= start.getTime()) {
            end.setUTCDate(end.getUTCDate() + 1)
        }
    } else {
        end.setHours(endHour)
        if (Number.isInteger(endHour)) {
            end.setMinutes(0)
        } else {
            end.setMinutes(Math.round((endHour - Math.floor(endHour)) * 60))
        }
        while (end.getTime() <= start.getTime()) {
            end.setDate(end.getDate() + 1)
        }
    }

    if (!(end > start)) return null
    return { start, end }
}

/**
 * Build a shift window anchored to a specific company-local calendar date. The selected date is
 * the day on which the shift starts; overnight shifts therefore end on the following day.
 * @param {{ start_hour: Number, end_hour: Number, gmt?: Number }} shift
 * @param {String} dateStr YYYY-MM-DD in the company's local calendar
 * @returns {{ start: Date, end: Date } | null}
 */
function getShiftWindowForDate(shift, dateStr) {
    if (!shift || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr || '')) return null
    const startHour = Number(shift.start_hour)
    const endHour = Number(shift.end_hour)
    if (!Number.isFinite(startHour) || !Number.isFinite(endHour)) return null

    const [year, month, day] = dateStr.split('-').map(Number)
    const gmt = Number(shift.gmt)
    let start
    let end

    if (Number.isFinite(gmt)) {
        const gmtMinutes = Math.round(gmt * 60)
        start = new Date(Date.UTC(year, month - 1, day, 0, Math.round(startHour * 60) - gmtMinutes))
        end = new Date(Date.UTC(year, month - 1, day, 0, Math.round(endHour * 60) - gmtMinutes))
        while (end.getTime() <= start.getTime()) end.setUTCDate(end.getUTCDate() + 1)
    } else {
        start = new Date(year, month - 1, day, 0, Math.round(startHour * 60))
        end = new Date(year, month - 1, day, 0, Math.round(endHour * 60))
        while (end.getTime() <= start.getTime()) end.setDate(end.getDate() + 1)
    }

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || !(end > start)) return null
    return { start, end }
}

/**
 * Company-local YYYY-MM-DD bounds for an active shift window (may span two calendar days).
 * @param {{ gmt?: Number }} shift
 * @param {{ start: Date, end: Date }} window
 * @returns {{ start: Date, end: Date, timeFrom: String, timeTo: String } | null}
 */
function getShiftQueryDateRange(shift, window) {
    if (!shift || !window) return null
    const timeFrom = utilService.companyLocalDateStr(window.start, shift.gmt)
    const timeTo = utilService.companyLocalDateStr(window.end, shift.gmt)
    if (!timeFrom || !timeTo) return null
    return { start: window.start, end: window.end, timeFrom, timeTo }
}